import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Shield, 
  FileCheck, 
  Globe, 
  Briefcase, 
  ClipboardList, 
  Users, 
  Receipt, 
  Calculator, 
  CheckCircle2, 
  FileText, 
  Clock, 
  Award, 
  ChevronRight, 
  MessageSquareShare
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ServiceData {
  title: string;
  category: string;
  desc: string;
  introduction: string;
  duration: string;
  urgency: string;
  difficulty: string;
  requirements: string[];
  steps: string[];
  benefits: string[];
  icon: React.ComponentType<any>;
  whatsAppText: string;
}

const serviceDetails: Record<string, ServiceData> = {
  "sbu": {
    title: "Pembuatan SBU",
    category: "Legalitas Konstruksi",
    desc: "Sertifikat Badan Usaha untuk legalitas perusahaan konstruksi Anda agar diakui negara dan siap mengikuti tender resmi.",
    introduction: "Sertifikat Badan Usaha (SBU) Jasa Konstruksi adalah bukti pengakuan formal atas tingkat kompetensi dan kemampuan usaha jasa konstruksi. Sesuai dengan UU Jasa Konstruksi No. 2 Tahun 2017 dan regulasi turunan Kementerian PUPR, kepemilikan SBU sangat krusial bagi setiap kontraktor (pelaksana) maupun pengawas/perencana (konsultan) yang ingin beroperasi secara legal di Indonesia, khususnya untuk mengikuti tender pengadaan barang dan jasa milik pemerintah, BUMN, maupun swasta berskala besar.",
    duration: "14 - 30 Hari Kerja",
    urgency: "Sangat Tinggi (Wajib Tender)",
    difficulty: "Kompleks (Perlu Verifikasi LSBU)",
    requirements: [
      "Akte Pendirian Badan Usaha (PT / CV / Koperasi) beserta semua Akte Perubahan terakhir.",
      "Surat Keputusan (SK) Pengesahan dari Kemenkumham.",
      "Nomor Induk Berusaha (NIB) dengan Klasifikasi Baku Lapangan Usaha Indonesia (KBLI) sektor Jasa Konstruksi.",
      "Kartu NPWP Perusahaan dan Surat Keterangan Domisili Usaha aktif.",
      "Laporan Keuangan Neraca & Laba Rugi yang telah diaudit oleh Akuntan Publik atau minimal laporan internal.",
      "Dokumen Penanggung Jawab Teknis Badan Usaha (PJTBU) yang memiliki SKK Konstruksi aktif sesuai bidang.",
      "Daftar pengalaman kerja perusahaan (jika ada) beserta kontrak kerja pendukung.",
      "Daftar aset peralatan utama yang dimiliki beserta bukti kepemilikan (nota/sertifikat)."
    ],
    steps: [
      "Konsultasi Kesiapan Dokumen: Tim ahli kami melakukan audit kelayakan berkas awal dan membantu penentuan klasifikasi KBLI serta asosiasi yang cocok.",
      "Pendaftaran Anggota Asosiasi: Membimbing pendaftaran perusahaan sebagai anggota asosiasi profesi konstruksi terakreditasi LPJK.",
      "Penyandingan Asesor & LSBU: Berkas diunggah lewat portal resmi untuk ditelaah dan diverifikasi oleh Lembaga Sertifikasi Badan Usaha (LSBU) pilihan.",
      "Uji Fisik & Validasi Kelayakan: Asesor melakukan verifikasi portofolio dan kelengkapan dokumen pendukung teknis.",
      "Penerbitan SBU Konstruksi: SBU berlogo LPJK & Kementerian PUPR diterbitkan secara elektronik dengan QR Code resmi yang valid selamanya dengan kewajiban daftar ulang berkala."
    ],
    benefits: [
      "Memenuhi Standar Regulasi Nasional secara mutlak sehingga aman dari sanksi hukum penutupan proyek.",
      "Membuka Akses Tender Terbatas Pemerintah, BUMN, maupun proyek swasta dengan nilai penawaran hingga ratusan miliar rupiah.",
      "Meningkatkan Kredibilitas & Trust Perusahaan di hadapan investor, supplier, serta perbankan dalam fasilitas pendanaan proyek.",
      "Memperoleh Perlindungan Hukum formal dari LPJK dalam batasan sub-klasifikasi keahlian yang dimiliki."
    ],
    icon: Shield,
    whatsAppText: "Halo Admin Azman204, saya tertarik untuk menggunakan Jasa Pembuatan SBU Konstruksi. Bagaimana langkah pembayarannya?"
  },
  "skk": {
    title: "Sertifikat SKK",
    category: "Kompetensi Tenaga Ahli",
    desc: "Sertifikat Kompetensi Kerja bagi tenaga ahli konstruksi sebagai bukti kompetensi dan keahlian formal.",
    introduction: "Sertifikat Kompetensi Kerja (SKK) Konstruksi (sebelumnya dikenal sebagai SKA/SKT) adalah bukti tertulis atas pengakuan kompetensi kerja tingkat nasional bagi tenaga kerja konstruksi. Hal ini merupakan standar kualifikasi mutlak yang wajib dipenuhi oleh PJTBU (Penanggung Jawab Teknis Badan Usaha) maupun tenaga terampil di lapangan sebagai syarat penerbitan SBU perusahaan, serta jaminan keselamatan kerja di area proyek jasa konstruksi nasional.",
    duration: "5 - 10 Hari Kerja",
    urgency: "Tinggi (Syarat Mutlak SBU)",
    difficulty: "Sedang (Ditentukan Jenjang Kualifikasi)",
    requirements: [
      "Kartu Tanda Penduduk (KTP) / Paspor Pemohon yang masih aktif.",
      "Salinan Ijazah Pendidikan Terakhir (Arsitektur / Teknik Sipil / Listrik / Mesin / Lingkungan) yang dilegalisir basah.",
      "Transkrip Nilai Akademik lengkap.",
      "Curriculum Vitae (CV) / Daftar Riwayat Hidup terstruktur yang menjabarkan rekam jejak kerja proyek.",
      "Surat Keterangan Pengalaman Kerja di bidang terkait, ditandatangani oleh pimpinan proyek atau direksi perusahaan.",
      "Pas Foto Digital terbaru (latar belakang merah / formal).",
      "Sertifikat pelatihan teknis (jika ada) untuk mendukung portfolio penilaian."
    ],
    steps: [
      "Asesmen Kualifikasi Awal: Tim kami menilai ijazah dan riwayat kerja Anda guna menentukan jenjang SKK (Jenjang 1-9, baik Operator, Teknisi, maupun Ahli).",
      "Penyusunan Portfolio Mandiri: Membantu menyusun formulir aplikasi diri (APL-01 & APL-02) agar memenuhi kriteria pengujian LSP.",
      "Uji Kompetensi Profesi: Pemohon mengikuti sesi ujian wawancara atau portofolio daring dengan Asesor dari Lembaga Sertifikasi Profesi (LSP) berlisensi BNSP.",
      "Verifikasi & Keputusan Pleno: Panitia LSP membahas hasil ujian dan menetapkan status kelulusan kompetensi pemohon.",
      "Penerbitan SKK LPJK: Penerbitan SKK terdaftar resmi di LPJK PUPR dan terintegrasi dengan database BNSP nasional."
    ],
    benefits: [
      "Memperoleh Pengakuan Profesional Secara Legal di seluruh ekosistem kerja konstruksi Indonesia.",
      "Memenuhi Syarat Administratif Utama sebagai tenaga ahli (PJTBU/PJBU) dalam proses pengurusan SBU Badan Usaha konstruksi.",
      "Menambah Nilai Jual & Peluang Karir pribadi untuk ditempatkan pada posisi strategis di proyek konstruksi skala multi-nasional.",
      "Menghindari Sanksi Pelanggaran Ketenagakerjaan konstruksi sesuai aturan Kementerian Ketenagakerjaan dan PUPR."
    ],
    icon: FileCheck,
    whatsAppText: "Halo Admin Azman204, saya ingin mengurus Sertifikat Kompetensi Kerja (SKK) Konstruksi untuk tenaga ahli saya. Mohon info syaratnya?"
  },
  "nib-oss": {
    title: "NIB & OSS",
    category: "Perizinan Dasar",
    desc: "Pengurusan Nomor Induk Berusaha (NIB) dan aktivasi portal perizinan OSS RBA berbasis risiko.",
    introduction: "Nomor Induk Berusaha (NIB) merupakan identitas pelaku usaha yang diterbitkan oleh Lembaga OSS setelah pelaku usaha melakukan pendaftaran. Berdasarkan sistem baru OSS RBA (Risk-Based Approach / Pendekatan Berbasis Risiko), NIB tidak sekadar berfungsi sebagai tanda daftar perusahaan, namun juga sebagai angka pengenal impor, hak akses kepabeanan, serta izin dasar untuk beroperasi bagi seluruh bidang usaha, termasuk UMKM hingga badan usaha investasi modal asing.",
    duration: "3 - 5 Hari Kerja",
    urgency: "Sangat Tinggi (Pintu Utama Legalitas)",
    difficulty: "Mudah - Sedang (Tergantung Pilihan Klasifikasi KBLI)",
    requirements: [
      "Data Diri Penanggung Jawab Perusahaan (NIK KTP / Paspor).",
      "Nomor HP dan Alamat Email aktif yang didaftarkan khusus untuk portal OSS.",
      "Kartu NPWP Pribadi atau NPWP Badan Hukum aktif.",
      "Akta Pendirian Pendirian PT / CV / Koperasi beserta nomor SK Pengesahan dari Kemenkumham.",
      "Detail Alamat Lokasi Usaha lengkap dengan koordinat posisi kantor/pabrik (GIS).",
      "Rencana Nilai Investasi perusahaan di luar aset tanah dan bangunan gedung.",
      "Klasifikasi Baku Lapangan Usaha Indonesia (KBLI) 2020 yang dipilih sesuai klaster bisnis utama."
    ],
    steps: [
      "Pemilihan Klasifikasi Bisnis yang Tepat: Sinkronisasi maksud tujuan di Akta Perusahaan dengan daftar KBLI 2020 demi mencegah konflik perizinan.",
      "Registrasi & Verifikasi Akun: Pembuatan username dan password portal OSS RBA menggunakan NIK Direktur/Kuasa Pengurus.",
      "Input Data Usaha & Keuangan: Pengisian nilai modal kerja, bidang usaha produktif, alamat operasional, dan parameter risiko.",
      "Penerbitan Dokumen Dasar: Sistem memproses dokumen secara otomatis untuk mengeluarkan NIB, Surat Pernyataan Kesanggupan Pengelolaan Lingkungan (SPPL), dan berkas pelengkap.",
      "Upgrade Sertifikat Standar (bila diperlukan): Menindaklanjuti Sertifikat Standar dari instansi kementerian jika usaha bernilai risiko sedang atau tinggi."
    ],
    benefits: [
      "Penyatuan Izin Terpadu: Satu nomor NIB mewakili Tanda Daftar Perusahaan (TDP), Angka Pengenal Importir (API), dan Akses Kepabeanan.",
      "Kepastian Hukum Berusaha: Perusahaan resmi terdaftar dalam direktori Kementerian Investasi/BKPM RI.",
      "Kemudahan Fasilitas Finansial: Mempercepat pembukaan rekening giro korporat dan mempermudah perolehan modal usaha perbankan.",
      "Kemudahan Mengurus Sertifikasi Lanjutan: Menjadi prasyarat dasar sebelum mengurus Sertifikat Halal, BPOM, Standar Nasional Indonesia (SNI), maupun SBU."
    ],
    icon: Globe,
    whatsAppText: "Halo Admin Azman204, saya ingin dibantu mengurus legalitas NIB & akun OSS RBA untuk usaha baru kami."
  },
  "konsultasi-perizinan": {
    title: "Konsultasi Perizinan",
    category: "Izin Teknis Proyek",
    desc: "Pendampingan lengkap pengurusan Persetujuan Bangunan Gedung (PBG), SLF, AMDAL, UKL/UPL dan analisis teknis.",
    introduction: "Proses perizinan pembangunan fisik bangunan komersial, industri, maupun pemukiman kini semakin ketat menyusul digantinya IMB menjadi PBG (Persetujuan Bangunan Gedung) serta kebutuhan SLF (Sertifikat Layak Fungsi). Layanan Konsultasi Perizinan kami hadir untuk menjembatani Anda dengan dinas teknis pemerintah (PUPR, Lingkungan Hidup, Perhubungan) guna memastikan dokumen perencanaan struktur, amdal, andalalin, serta instalasi proteksi kebakaran lolos uji pemenuhan keandalan gedung.",
    duration: "30 - 60 Hari Kerja",
    urgency: "Sedang - Tinggi (Kepatuhan Konstruksi)",
    difficulty: "Sangat Tinggi (Memerlukan Kajian Ahli)",
    requirements: [
      "Dokumen Sertifikat Hak Milik (SHM) / bukti kepemilikan lahan yang sah.",
      "Gambar Detail Desain Arsitektur, Gambar Struktur, dan Rencana Utilitas MEP gedung.",
      "Kajian Perhitungan Struktur Beton/Baja yang ditandatangani oleh Tenaga Ahli bersertifikat SKK Ahli Struktur.",
      "Hasil Penyelidikan Tanah (Sondir / Boring Test) dari laboratorium geoteknik tepercaya.",
      "Surat Keputusan Dokumen Lingkungan (AMDAL, UKL-UPL, atau SPPL).",
      "Dokumen Analisis Dampak Lalu Lintas (Andalalin) untuk kategori bangunan wajib andalalin."
    ],
    steps: [
      "Due Diligence Lokasi & Zoning: Memeriksa zonasi tata ruang kota (RTRW) guna memastikan izin fungsi bangunan dapat dikeluarkan.",
      "Penyusunan Berkas Kajian Teknis: Tim konsultan ahli kami melengkapi dokumen struktural, perhitungan kehandalan kelistrikan, tata udara, serta sistem sanitasi gedung.",
      "Registrasi SIMBG: Mengunggah data permohonan ke portal Sistem Informasi Manajemen Bangunan Gedung (SIMBG) kementerian.",
      "Asistensi Sidang Tim Ahli Bangunan Gedung (TABG): Menghadiri dan mempertahankan konsep perencanaan struktur saat hearing dengan asosiasi profesi teknis ditunjuk pemerintah.",
      "Penerbitan PBG & SLF: Dinas Penanaman Modal (DPMPTSP) merilis PBG sebagai izin konstruksi, disusul SLF seusai gedung selesai dibangun secara fisik."
    ],
    benefits: [
      "Mencegah Penyegelan Proyek: Pembangunan fisik berjalan mulus tanpa risiko dihentikan satpol PP atau dinas pengawas bangunan setempat.",
      "Andal & Kompeten: Memastikan struktur gedung dijamin keamanannya oleh perhitungan ahli sipil berlisensi sehingga aman dari kecelakaan.",
      "Mempermudah Transaksi Penjualan/Sewa: SLF merupakan syarat wajib bagi perbankan sebelum merilis kredit pemilikan ruko, hotel, atau apartemen komersial.",
      "Asuransi Kehilangan/Kerusakan: Memungkinkan bangunan memperoleh premi pertanggungan asuransi bencana dan risiko kebakaran secara penuh."
    ],
    icon: Briefcase,
    whatsAppText: "Halo Admin Azman204, saya butuh pendampingan dan konsultasi mengenai perizinan PBG/SLF untuk proyek kami."
  },
  "pendampingan-proyek": {
    title: "Pendampingan Proyek",
    category: "Manajemen Proyek",
    desc: "Bantuan profesional untuk administrasi kontrak, penyusunan Kurva S, monitoring progres, dan audit mutu berkala di lapangan.",
    introduction: "Keberhasilan suatu proyek konstruksi diukur dari ketepatan waktu selesai, mutu yang sesuai rencana, dan anggaran biaya yang efisien (On Time, On Spec, On Budget). Layanan Pendampingan Proyek kami memberikan dukungan profesional berupa pengawasan administrasi kontrak, penyusunan jadwal logistik, manajemen klaim tambah-kurang pekerjaan (CCO), serta kontrol lapangan berkala agar kontraktor terhindar dari sanksi denda keterlambatan (liquidated damages) atau sengketa wanprestasi.",
    duration: "Sesuai Kontrak Pekerjaan Proyek",
    urgency: "Sedang (Kesuksesan Pelaksanaan)",
    difficulty: "Tinggi (Memerlukan Pemahaman Teknis & Kurva S)",
    requirements: [
      "Salinan Dokumen Kontrak Kerja Konstruksi (Surat Perjanjian Kerja / SPK).",
      "Dokumen Gambar Rencana Kerja (As-Designed Drawings) dan Rencana Anggaran Biaya (RAB) Penawaran.",
      "Daftar Bill of Quantities (BoQ) resmi.",
      "Jadwal peluncuran proyek dan data daftar tenaga kerja kunci di lapangan.",
      "Spesifikasi Teknis Material yang disyaratkan pihak Pengguna Jasa/Klien."
    ],
    steps: [
      "Review Kontrak & Identifikasi Resiko: Membedah isi kontrak, pasal-pasal pinalti denda, tata cara eskalasi harga, dan prosedur klaim variasi.",
      "Penyusunan baseline Kurva S: Menggunakan Microsoft Project atau Primavera untuk memetakan ketergantungan durasi pekerjaan di lapangan.",
      "Monitoring Lapangan Berkala: Tim kami mendatangi lokasi guna memeriksa progres mingguan, mengidentifikasi keterlambatan pasokan, dan menganalisis deviasi.",
      "Manajemen Amandemen & CCO: Membantu menyiapkan data teknis (justifikasi teknis) apabila terjadi perubahan desain untuk diajukan sebagai Contract Change Order (CCO).",
      "Penyusunan Berkas Serah Terima (PHO & FHO): Mendampingi penyusunan kelengkapan berita acara pemeriksaan fisik, uji fungsi peralatan, dan dokumen as-built drawings."
    ],
    benefits: [
      "Menghindari Cost Overrun: Memantau pengeluaran anggaran agar tidak melebihi alokasi dana operasional proyek.",
      "Deteksi Dini Keterlambatan: Membantu mencarikan solusi percepatan (crashing schedule/lembur tertata) bila grafik Kurva S menunjukkan keterlambatan di atas toleransi.",
      "Kualitas Mutu Terjamin: Pengawasan independen meminimalisir kegagalan bangunan akibat kelalaian tukang.",
      "Administrasi Kontrak Rapi: Menjamin penagihan sertifikat bulanan (monthly certificate) lancar diapprove oleh pemilik proyek."
    ],
    icon: ClipboardList,
    whatsAppText: "Halo Admin Azman204, saya ingin jasa Pendampingan Pelaksanaan Proyek (administrasi, Kurva S, audit berkala)."
  },
  "penyusunan-dokumen-penawaran": {
    title: "Penyusunan Dokumen Penawaran",
    category: "Spesialis Tender / Proposal",
    desc: "Penyusunan berkas bid administratif, proposal teknis yang kaya visual, dan perhitungan analisis harga bersaing demi kelulusan tender.",
    introduction: "Kemenangan dalam proses tender proyek konstruksi dan jasa konsultansi sangat bergantung pada kualitas Dokumen Penawaran Anda. Seringkali, perusahaan yang memiliki harga bersaing harus kalah di tahap awal akibat kesalahan administratif sepele atau metode pelaksanaan teknis yang kurang realistis serta miskin dokumentasi visual. Tim Spesialis Tender kami siap membantu merancang berkas penawaran administratif, menyusun metode pelaksanaan yang meyakinkan, serta menghitung Analisa Harga Satuan Pekerjaan (AHSP) yang tajam.",
    duration: "5 - 7 Hari Kalender",
    urgency: "Sangat Tinggi (Dikejar Batas Upload LPSE)",
    difficulty: "Sangat Tinggi (Menyangkut Strategi Kelulusan)",
    requirements: [
      "Dokumen Rencana Kerja dan Syarat-Syarat (RKS) / Dokumen Pemilihan Tender lengkap.",
      "Gambar Perencanaan Teknis (DED) dari Pemberi Tugas.",
      "Daftar Isian Bill of Quantities (BoQ) format Excel.",
      "Kelengkapan legalitas perusahaan pengikut tender (SBU, SKK Tenaga Ahli, Pajak Terakhir).",
      "Daftar peralatan utama yang diwajibkan dalam Lembar Data Pemilihan (LDP)."
    ],
    steps: [
      "Pre-Bid Analysis & Aanwijzing: Membedah aturan LDP (Lembar Data Pemilihan) dan batas gugur teknis, serta merumuskan pertanyaan taktis saat rapat penjelasan (Aanwijzing).",
      "Penyusunan Metode Kerja Visual: Merancang metode pengerjaan tanah, struktur, finishing secara deskriptif dilengkapi gambar visualisasi tahapan konstruksi.",
      "Penyusunan Jadwal & Manajemen Peralatan: Menghitung target kurva S penawaran dan mencocokkan ketersediaan logistik serta peralatan.",
      "Analisa Harga Satuan (AHSP): Memformulasikan koefisien upah, bahan, peralatan merujuk Permen PUPR bersanding harga pasar lokal yang ekonomis.",
      "Final Packaging & Enkripsi Portal: Melakukan kompilasi seluruh PDF penawaran administrasi, teknis, dan keuangan sesuai instruksi pengunggahan ke portal LPSE (SPSE)."
    ],
    benefits: [
      "Lolos Seleksi Administrasi & Teknis: Dokumen diklaim rapi, tumpang tindih berkas dihilangkan, mematuhi semua checklist Lembar Data Pemilihan.",
      "Kombinasi Win-Win: Membantu memformulasi diskon penawaran cerdas agar mendapat ranking teratas tanpa merugikan margin keuntungan kontraktor.",
      "Metodologi Meyakinkan: Metode kerja didesain profesional sehingga reviewer/pokja tender yakin akan kompetensi eksekusi lapangan perusahaan Anda.",
      "Tidur Nyenyak Menjelang Batas Upload: Menghapus stres keterlambatan upload karena tim ahli kami bergerak cepat sesuai schedule ketat."
    ],
    icon: Users,
    whatsAppText: "Halo Admin Azman204, saya sedang mengikuti tender dan membutuhkan bantuan Penyusunan Dokumen Penawaran teknis/harga."
  },
  "pajak-badan-pribadi": {
    title: "Pajak Badan & Orang Pribadi",
    category: "Kepatuhan Perpajakan",
    desc: "Layanan jasa penyiapan, rekonsiliasi fiskal, perhitungan, dan pelaporan SPT Tahunan Badan Hukum maupun SPT Orang Pribadi dengan patuh.",
    introduction: "Peraturan perpajakan di Indonesia pasca disahkannya UU Harmonisasi Peraturan Perpajakan (HPP) menjadi lebih dinamis. Pelaporan Surat Pemberitahuan (SPT) Tahunan yang tidak akurat dapat memicu terbitnya Surat Permintaan Penjelasan atas Data dan/atau Keterangan (SP2DK) dari KPP Pratama. Layanan Perpajakan kami memayungi penyusunan laporan keuangan fiskal, perhitungan PPh kurang bayar, serta pendampingan pelaporan SPT Badan Hukum maupun Orang Pribadi agar perusahaan Anda terhindar dari denda denda administrasi berat.",
    duration: "5 - 12 Hari Kerja",
    urgency: "Tinggi (Kepatuhan Pajak Negara)",
    difficulty: "Sedang - Tinggi (Menuntut Ketelitian Data Keuangan)",
    requirements: [
      "Laporan Keuangan Komersil (Neraca Fisik, Laporan Rugi Laba) untuk tahun buku berjalan.",
      "Rekening Koran dari semua bank operasional dalam jangka waktu 1 tahun penuh.",
      "Daftar Rincian Peredaran Bruto Bulanan (Omset) beserta bukti setor PPh Final (jika menggunakan skema UMKM).",
      "Daftar Aktiva / Aset tetap perusahaan dilengkapi tanggal pembelian untuk pencatatan tarif penyusutan.",
      "Bukti Pemotongan/Pemungutan Pajak oleh pihak lain (Bukti Potong PPh 21, PPh 23, PPh Pasal 4 ayat 2).",
      "KTP Penanggung Jawab / Direksi serta Kartu Keluarga beserta daftar rincian harta kekayaan pribadi."
    ],
    steps: [
      "Rekonsiliasi Fiskal Terpadu: Melakukan koreksi fiskal positif/negatif atas laporan keuangan komersil agar selaras dengan aturan perpajakan.",
      "Simulasi & Optimalisasi Beban (Tax Planning): Menentukan alternatif metode pembebanan biaya perpajakan yang sah guna meminimalkan sanksi kurang bayar.",
      "Pengisian E-Form Keuangan: Menyiapkan lembar isian lampiran-lampiran SPT Badan (Formulir 1771) atau Orang Pribadi (1770) di aplikasi DJP Online.",
      "Penyetoran Pajak Terutang: Membantu generate kode billing dan melengkapi proses penyetoran beban PPh yang masih terutang.",
      "Submit & Arsip BPE: Mengirim berkas secara digital ke portal DJP Online dan menyerahkan Bukti Penerimaan Elektronik (BPE) resmi sebagai bukti lapor sah."
    ],
    benefits: [
      "Menghindari Denda & Bunga: Kepastian pelaporan sebelum jatuh tempo (31 Maret untuk Orang Pribadi, 30 April untuk Badan) melindungi dari sanksi.",
      "Tax Planning Legal: Memanfaatkan komponen insentif pajak pengurangan tarif perpajakan PPh Pasal 31E UU PPh secara sah dan optimal.",
      "Ketenangan Berbisnis: Memperkecil peluang dipanggil penyidik pajak akibat ketidakakuratan penulisan klasifikasi harta atau aset.",
      "Dokumentasi Keuangan Prasyarat Kredit: Laporan SPT Tahunan yang rapi merupakan syarat vital mendapatkan kenaikan limit kredit bank atau pembiayaan modal kerja."
    ],
    icon: Receipt,
    whatsAppText: "Halo Admin Azman204, saya ingin berkonsultasi mengenai laporan Pajak Tahunan Badan Usaha / Orang Pribadi."
  },
  "spt-masa-efaktur": {
    title: "SPT Masa & E-Faktur",
    category: "Administrasi Rutin Bulanan",
    desc: "Manajemen rutin bulanan pembuatan Faktur Pajak Keluaran, persetujuan Faktur Pajak Masukan, dan pelaporan SPT Masa PPN unifikasi secara konsisten.",
    introduction: "Bagi Pengusaha Kena Pajak (PKP), melaporkan SPT Masa PPN dan Pajak Unifikasi bulanan merupakan kewajiban yang tidak boleh dilewatkan setiap akhir bulan berikutnya. Keterlambatan pelaporan atau kesalahan dalam penerbitan Faktur Pajak Elektronik (e-Faktur) akan berujung pada sanksi denda per fatur yang sangat mengganggu arus kas operasional usaha. Layanan Bulanan kami membantu mengelola seluruh berkas administrasi e-faktur bulanan Anda, melaporkan pajak unifikasi (PPh 21, 23, 4 ayat 2) secara tertata dan patuh hukum.",
    duration: "Pelayanan Bulanan (Rutin Berkala)",
    urgency: "Sangat Tinggi (Setiap Bulan)",
    difficulty: "Sedang (Rutin & Presisi Sistem)",
    requirements: [
      "Akun DJP Online aktif beserta file Sertifikat Elektronik (.p12) dan Passphrase-nya.",
      "Daftar Invoices Penjualan / Rincian Tagihan kepada customer (untuk dasar E-Faktur Keluaran).",
      "Kumpulan Faktur Pajak Masukan yang diterima dari supplier / vendor.",
      "Data Slip ringkasan gaji bulanan karyawan (untuk perhitungan PPh Pasal 21 Masa).",
      "Akun e-Faktur Desktop atau akses ke Web-Efaktur DJP yang telah terkonfigurasi."
    ],
    steps: [
      "Penerbitan E-Faktur Keluaran: Mengunggah data tagihan penjualan ke server DJP guna memproses QR Code Faktur Pajak Keluaran sebelum tanggal 15 bulan berikutnya.",
      "Kompilasi Masukan (Kredit Pajak): Melakukan scanning dan persetujuan terhadap Faktur Pajak Masukan dari transaksi pembelian bahan material proyek.",
      "Perhitungan Selisih PPN: Menjumlahkan PPN Keluaran dikurangi PPN Masukan untuk mendapatkan besaran PPN Kurang/Lebih Bayar bulanan perusahaan.",
      "Penyiapan Pajak Unifikasi: Mengalkulasi potong-pungut pajak PPh 21 (karyawan/tukang), PPh 23 (sewa alat), PPh 4 ayat 2 (kontrak jasa konstruksi).",
      "Pelaporan SPT Masa Bulanan: Mengirimkan laporan SPT Masa PPN via Web-Efaktur serta SPT unifikasi via portal unifikasi DJP sebelum hari terakhir bulan berikutnya."
    ],
    benefits: [
      "Bebas Denda Administrasi e-Faktur: Menghilangkan risiko denda denda pelaporan telat bulanan.",
      "Arus Kas Optimal: Manajemen kredit pajak PPN masukan yang cermat mengurangi beban kas setoran bulanan seminimal mungkin.",
      "Kepatuhan Terintegrasi: Rekonsiliasi bulanan memudahkan penyusunan laporan pajak tahunan secara mutlak tanpa selisih data drastis.",
      "Keamanan Transaksi B2B: Klien/pelanggan Anda tenang karena faktur pajak terbit secara presisi dan tepat waktu, tidak menghambat klaim pemotongan pajak mereka."
    ],
    icon: Calculator,
    whatsAppText: "Halo Admin Azman204, saya butuh jasa pelaporan rutin bulanan SPT Masa PPN dan manajemen E-Faktur."
  }
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = slug ? serviceDetails[slug] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background">
        <div className="h-16 w-16 bg-red-500/10 text-red-500 rounded-2xl flex items-center justify-center mb-6 border border-red-500/20">
          <Shield className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-foreground">Detail Layanan Tidak Ditemukan</h2>
        <p className="text-foreground/50 max-w-sm mb-6 text-sm">Maaf, halaman rincian layanan administrasi yang Anda cari tidak tersedia atau telah dipindahkan.</p>
        <Button onClick={() => navigate('/services')} className="bg-primary hover:bg-primary-hover text-primary-foreground font-bold rounded-xl px-6 h-12">
          Kembali ke Layanan
        </Button>
      </div>
    );
  }

  const handleOrderWhatsApp = () => {
    const encodedMessage = encodeURIComponent(service.whatsAppText);
    const waUrl = `https://wa.me/6281261089400?text=${encodedMessage}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen bg-background">
      {/* Dynamic Header Overlay */}
      <div className="bg-navy pt-28 md:pt-36 pb-32 md:pb-40 px-6 relative overflow-hidden text-white border-b border-border/10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-gold transition-colors mb-8 text-xs font-black uppercase tracking-[0.2em] group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Kembali ke Layanan
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3.5 py-1 bg-gold/20 text-gold rounded-full text-[10px] font-black uppercase tracking-widest border border-gold/30">
              {service.category}
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 space-y-4">
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight tracking-tight">
                Layanan <span className="text-gold">{service.title}</span>
              </h1>
              <p className="text-white/70 text-base md:text-lg max-w-3xl leading-relaxed text-justify">
                {service.desc}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-24 relative z-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Detailed Content Panel (Col span 8) */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-card border border-border rounded-[2rem] p-8 md:p-12 shadow-2xl space-y-10">
              
              {/* Introduction Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gold">
                  <Award className="h-5 w-5" />
                  <h3 className="text-lg font-black uppercase tracking-wider">Tentang Layanan</h3>
                </div>
                <p className="text-foreground/75 leading-relaxed text-sm md:text-base text-justify">
                  {service.introduction}
                </p>
              </div>

              {/* Requirement Section */}
              <div className="space-y-6 pt-6 border-t border-border/50">
                <div className="flex items-center gap-2 text-gold">
                  <FileText className="h-5 w-5" />
                  <h3 className="text-lg font-black uppercase tracking-wider">Persyaratan & Kelengkapan Berkas</h3>
                </div>
                <p className="text-foreground/50 text-xs italic -mt-2">Silakan persiapkan berkas-berkas berikut untuk mempercepat proses administrasi:</p>
                <div className="grid grid-cols-1 gap-4">
                  {service.requirements.map((req, i) => (
                    <div key={i} className="flex gap-3 items-start p-4 bg-muted/20 border border-border/50 rounded-2xl hover:bg-muted/35 transition-colors">
                      <div className="h-6 w-6 rounded-full bg-gold/10 text-gold flex items-center justify-center shrink-0 font-bold text-xs">
                        {i + 1}
                      </div>
                      <p className="text-foreground/80 text-sm leading-relaxed">{req}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Steps of Process Section */}
              <div className="space-y-6 pt-6 border-t border-border/50">
                <div className="flex items-center gap-2 text-gold">
                  <Clock className="h-5 w-5" />
                  <h3 className="text-lg font-black uppercase tracking-wider">Alur & Tahapan Kerja</h3>
                </div>
                <div className="space-y-4 relative pl-4 border-l-2 border-gold/15 ml-3">
                  {service.steps.map((step, i) => {
                    const [stepTitle, stepDesc] = step.split(': ');
                    return (
                      <div key={i} className="relative group/step pb-2">
                        {/* Circle Bullet */}
                        <div className="absolute -left-[25px] top-1 h-4.5 w-4.5 rounded-full bg-navy border-2 border-gold flex items-center justify-center text-[8px] text-gold font-bold"></div>
                        <div className="space-y-1 pl-4">
                          <h4 className="font-bold text-foreground text-sm. md:text-base group-hover/step:text-gold transition-colors">{stepTitle}</h4>
                          {stepDesc && <p className="text-foreground/60 text-xs md:text-sm leading-relaxed text-justify">{stepDesc}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Benefits Section */}
              <div className="space-y-6 pt-6 border-t border-border/50">
                <div className="flex items-center gap-2 text-gold">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  <h3 className="text-lg font-black uppercase tracking-wider">Manfaat Utama</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, i) => (
                    <div key={i} className="flex gap-3.5 items-start p-5 bg-card border border-border hover:border-gold/20 transition-all rounded-[1.5rem] shadow-sm">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                      <div>
                        {benefit.includes(': ') ? (
                          <>
                            <p className="font-bold text-foreground text-sm leading-tight mb-1">{benefit.split(': ')[0]}</p>
                            <p className="text-foreground/60 text-xs leading-relaxed">{benefit.split(': ')[1]}</p>
                          </>
                        ) : (
                          <p className="text-foreground/80 text-sm leading-relaxed">{benefit}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Quick Specifications Sidebar (Col span 4) */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            <div className="bg-card border border-border rounded-[2rem] p-6 shadow-2xl space-y-6">
              <div className="space-y-4">
                <h4 className="text-xs font-black uppercase tracking-[0.2em] text-foreground/45 border-b border-border/60 pb-3">Spesifikasi Layanan</h4>
                
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-border/30">
                    <span className="text-foreground/50">Estimasi Selesai</span>
                    <span className="font-bold text-foreground">{service.duration}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border/30">
                    <span className="text-foreground/50">Tingkat Urgensi</span>
                    <span className="font-bold text-gold">{service.urgency}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-foreground/50">Kompleksitas</span>
                    <span className="font-bold text-foreground">{service.difficulty}</span>
                  </div>
                </div>
              </div>

              {/* CTA Form / Actions */}
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={handleOrderWhatsApp}
                  className="w-full h-14 rounded-2xl bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-sm tracking-wider uppercase flex items-center justify-center gap-2.5 shadow-lg shadow-[#25D366]/20 hover:scale-[1.02] transition-all"
                >
                  <MessageSquareShare className="h-5 w-5" /> Hubungi via WhatsApp
                </Button>
                
                <p className="text-[10px] text-center text-foreground/40 leading-relaxed px-2">
                  Konsultasi awal gratis! Tim ahli kami akan memandu Anda melalui pengiriman berkas digital secara aman.
                </p>
              </div>
            </div>

            <div className="bg-gold/5 border border-gold/20 rounded-[2rem] p-6 space-y-4">
              <div className="h-10 w-10 bg-gold/15 rounded-xl flex items-center justify-center text-gold">
                <ServiceIcon className="h-5 w-5" />
              </div>
              <h4 className="font-extrabold text-foreground text-sm uppercase tracking-wider">Garansi Transparansi Berkas</h4>
              <p className="text-foreground/60 text-xs leading-relaxed text-justify">
                Seluruh pengurusan dilakukan secara legal langsung ke instansi / lembaga berwenang (LPJK PUPR, LSP terakreditasi, BKPM OSS RBA). Transparansi biaya layanan dan pengembalian berkas dijamin 100% aman bersama Azman204.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
